import {
  handoverCardsAttribute,
  handoverDueAttribute,
} from "../content/lianxi/exercise-fields.js";
import type { RecordAnswer } from "./lianxi-post.js";

/**
 * How many cards a finished Exercise 练习 put in 复习 and when the first
 * comes due, as the page hears it (#1209).
 *
 * A press's answer carries both once the finish has handed the material over,
 * and the block carries them on the way in for an Exercise finished before
 * the page was opened. A row handed over before the count was kept carries
 * neither, and the finish line then says what it said before.
 * `src/user/handover-answer.ts` is the other end.
 */
export interface HandoverCount {
  readonly cards: number;
  readonly dueAt: Date;
}

/**
 * The count and the instant, or nothing where either is missing or malformed.
 * @param cards - The count, as a number or an attribute's text.
 * @param dueAt - The instant, as ISO 8601 text.
 * @returns The count, or nothing.
 */
export function handoverCountOf(
  cards: unknown,
  dueAt: unknown,
): HandoverCount | undefined {
  const count =
    typeof cards === "string" || typeof cards === "number"
      ? Number(cards)
      : Number.NaN;
  const due = typeof dueAt === "string" ? new Date(dueAt) : undefined;

  return Number.isInteger(count) &&
    count > 0 &&
    due !== undefined &&
    !Number.isNaN(due.getTime())
    ? { cards: count, dueAt: due }
    : undefined;
}

/** The count a block carries, as `./lianxi-place-write.ts` wrote it. */
export function blockHandoverCount(
  block: HTMLElement,
): HandoverCount | undefined {
  return handoverCountOf(
    block.dataset[handoverCardsAttribute],
    block.dataset[handoverDueAttribute],
  );
}

/** The count a press's answer carries. */
export function answerHandoverCount(
  answer: RecordAnswer | undefined,
): HandoverCount | undefined {
  return handoverCountOf(answer?.handedOverCards, answer?.handedOverDueAt);
}
